import { windowCellBorderThickness, zFightingFixDistance } from "@dextall/panels-defaults";
import {
    getLouvreOverlapsSetting,
    getWindowOverlapsSetting,
    type SystemSettings,
    type WindowOverlapsSetting,
} from "../../../responses/systemSettings";
import { BoundaryLoop } from "../boundaries/boundaryLoop";
import { BoundaryLoopsFactory } from "../boundaries/boundaryLoopsFactory";
import colorsFactory from "../colors/colorsFactory";
import {
    CladdingCellExtrusionGeometryFactory,
    ExtrusionGeometry,
} from "../geometry/claddingCellExtrusionGeometryFactory";
import type { CladdingCellStyle, IPanelSourceLouvre, IPanelSourceOpening } from "../../../responses/panelSource";

export class PanelWindowSill {
    private readonly windowSillOffset = Autodesk.Viewing.Private.convertUnits("inch", "ft", 1, 2);
    private readonly boundaryLoop: BoundaryLoop;
    private readonly material: THREE.Material;

    private constructor(
        openingBox: THREE.Box3,
        sillColor: number,
        overlapsSetting: WindowOverlapsSetting,
        frameOffsetBottom: number,
        windowSillHeight: number,
        transform: THREE.Matrix4,
        boundaryLoopsFactory: BoundaryLoopsFactory,
        private readonly claddingExtrusionFactory: CladdingCellExtrusionGeometryFactory,
    ) {
        const windowSillStartPoint = openingBox.min.y + frameOffsetBottom;

        const box = new THREE.Box3(
            new THREE.Vector3(
                openingBox.min.x + overlapsSetting.left,
                windowSillStartPoint - windowSillHeight,
                0,
            ),
            new THREE.Vector3(openingBox.max.x - overlapsSetting.right, windowSillStartPoint),
        );

        this.boundaryLoop = boundaryLoopsFactory.createLoopFromBox(box, transform);
        this.material = colorsFactory.getColor(`RAL ${sillColor}`)!;
    }

    static createWindowSill(
        panelWindowDefinition: IPanelSourceOpening,
        systemSettings: SystemSettings,
        transform: THREE.Matrix4,
        boundaryLoopsFactory: BoundaryLoopsFactory,
        claddingExtrusionFactory: CladdingCellExtrusionGeometryFactory,
        panelStyle: CladdingCellStyle,
    ): PanelWindowSill {
        const windowOverlapsSetting = getWindowOverlapsSetting(systemSettings, panelStyle);

        return new PanelWindowSill(
            panelWindowDefinition.box,
            panelWindowDefinition.windowSillColor,
            windowOverlapsSetting,
            systemSettings.windowFrameOffset.bottom,
            systemSettings.windowSillHeight,
            transform,
            boundaryLoopsFactory,
            claddingExtrusionFactory,
        );
    }

    static createLouvreSill(
        panelLouvreDefinition: IPanelSourceLouvre,
        systemSettings: SystemSettings,
        transform: THREE.Matrix4,
        boundaryLoopsFactory: BoundaryLoopsFactory,
        claddingExtrusionFactory: CladdingCellExtrusionGeometryFactory,
        panelStyle: CladdingCellStyle,
    ): PanelWindowSill {
        const louvreOverlapsSetting = getLouvreOverlapsSetting(systemSettings, panelStyle);

        return new PanelWindowSill(
            panelLouvreDefinition.box,
            panelLouvreDefinition.sillColor,
            louvreOverlapsSetting,
            systemSettings.louvreFrameOffset.bottom,
            systemSettings.windowSillHeight,
            transform,
            boundaryLoopsFactory,
            claddingExtrusionFactory,
        );
    }

    createGeometry(): ExtrusionGeometry {
        return this.claddingExtrusionFactory.createCladdingCellExtrusion(this.boundaryLoop, {
            thickness: this.windowSillOffset,
            material: this.material,
            zOffset: windowCellBorderThickness + zFightingFixDistance,
        });
    }
}
